import { Color, Vector3, Vector4 } from 'three';
import type { IUniform, Texture } from 'three';
import type { MapDef } from '@cloudpearl/config';

/**
 * One set of uniform objects shared by reference across every world material, so the
 * atmosphere / sun / wind is updated once per frame no matter how many materials exist.
 */
export class SharedUniforms {
  readonly uTime: IUniform<number> = { value: 0 };
  readonly uSunDir: IUniform<Vector3> = { value: new Vector3(0.3, 0.8, -0.4).normalize() };
  readonly uSunColor: IUniform<Color> = { value: new Color(1, 1, 1) };
  readonly uSunGlow: IUniform<Color> = { value: new Color(1, 0.8, 0.6) };
  readonly uSkyTop: IUniform<Color> = { value: new Color() };
  readonly uSkyHorizon: IUniform<Color> = { value: new Color() };
  readonly uSkyBottom: IUniform<Color> = { value: new Color() };
  readonly uAmbSky: IUniform<Color> = { value: new Color() };
  readonly uAmbGround: IUniform<Color> = { value: new Color() };
  readonly uAmbIntensity: IUniform<number> = { value: 1 };
  readonly uShadowTint: IUniform<Color> = { value: new Color() };
  readonly uWrap: IUniform<number> = { value: 0.4 };
  readonly uFogColor: IUniform<Color> = { value: new Color() };
  readonly uFogParams: IUniform<Vector4> = { value: new Vector4(0.001, 0.01, 0, 0.8) };
  readonly uSunScatter: IUniform<number> = { value: 0.5 };
  readonly uWind: IUniform<Vector4> = { value: new Vector4(1, 0, 1, 1) };
  readonly uPlayerPos: IUniform<Vector3> = { value: new Vector3() };
  readonly uPlayerVel: IUniform<Vector4> = { value: new Vector4() };
  /** Irradiance SH (9 × RGB). Seeded from the palette's sky/ground colours; replaced by the captured environment. */
  readonly uSH: IUniform<Vector3[]> = { value: Array.from({ length: 9 }, () => new Vector3()) };
  /** The pearl's trail (sand grooves, flattened grass). Set by the host; disabled until then. */
  readonly tTrail: IUniform<Texture | null> = { value: null };
  readonly uTrailInfo: IUniform<Vector4> = { value: new Vector4(0.25, 128, 0, 0) };
  readonly uTrailWin: IUniform<Vector4> = { value: new Vector4(0, 0, 64, 0) };
  /** Atmosphere: cloud-shadow strength, mist density, mist base height, mist thickness. */
  readonly uAtmos: IUniform<Vector4> = { value: new Vector4(0, 0, 0, 1) };

  /** Sun azimuth is relative to the track's starting direction (−Z). */
  applyMap(map: MapDef): void {
    const p = map.palette;
    const l = map.lighting;
    const el = (l.sunElevation * Math.PI) / 180;
    const az = (l.sunAzimuth * Math.PI) / 180;
    // Azimuth 0 = sun in the north (−Z); positive turns east (+X).
    this.uSunDir.value.set(Math.sin(az) * Math.cos(el), Math.sin(el), -Math.cos(az) * Math.cos(el)).normalize();
    this.uSunColor.value.set(p.sun).multiplyScalar(l.sunIntensity);
    this.uSunGlow.value.set(p.sunGlow);
    this.uSkyTop.value.set(p.skyTop);
    this.uSkyHorizon.value.set(p.skyHorizon);
    this.uSkyBottom.value.set(p.skyBottom);
    this.uAmbSky.value.set(p.ambientSky);
    this.uAmbGround.value.set(p.ambientGround);
    this.uAmbIntensity.value = l.ambientIntensity;
    this.uShadowTint.value.set(p.shadow);
    this.uWrap.value = l.wrap;
    this.uFogColor.value.set(p.fog);
    this.uFogParams.value.set(map.fog.density, map.fog.heightFalloff, map.fog.baseHeight, map.fog.skyBlend);
    this.uSunScatter.value = map.fog.sunScatter;
    const wh = (map.wind.heading * Math.PI) / 180;
    this.uWind.value.set(Math.sin(wh), -Math.cos(wh), map.wind.strength, map.wind.gustiness);
    const at = map.atmosphere;
    this.uAtmos.value.set(at.cloudShadows, at.mist.density, at.mist.base, Math.max(1, at.mist.height));
    // Default ambient: a sky/ground gradient expressed as SH, E(n) = A + B·n.y (×π so the
    // shader's E/π matches the palette colours).
    const sky = this.uAmbSky.value;
    const ground = this.uAmbGround.value;
    const sh = this.uSH.value;
    for (const v of sh) v.set(0, 0, 0);
    sh[0]!.set(((sky.r + ground.r) / 2) * Math.PI, ((sky.g + ground.g) / 2) * Math.PI, ((sky.b + ground.b) / 2) * Math.PI).divideScalar(0.886227);
    sh[1]!.set(((sky.r - ground.r) / 2) * Math.PI, ((sky.g - ground.g) / 2) * Math.PI, ((sky.b - ground.b) / 2) * Math.PI).divideScalar(1.023328);
  }

  /** Install SH coefficients captured from the rendered environment. */
  setSH(coefficients: ArrayLike<{ x: number; y: number; z: number }>): void {
    for (let i = 0; i < 9; i++) {
      const c = coefficients[i]!;
      this.uSH.value[i]!.set(c.x, c.y, c.z);
    }
  }

  /** Spread into a ShaderMaterial's uniforms (by reference). */
  all(): Record<string, IUniform> {
    return {
      uTime: this.uTime,
      uSunDir: this.uSunDir,
      uSunColor: this.uSunColor,
      uSunGlow: this.uSunGlow,
      uSkyTop: this.uSkyTop,
      uSkyHorizon: this.uSkyHorizon,
      uSkyBottom: this.uSkyBottom,
      uAmbSky: this.uAmbSky,
      uAmbGround: this.uAmbGround,
      uAmbIntensity: this.uAmbIntensity,
      uShadowTint: this.uShadowTint,
      uWrap: this.uWrap,
      uFogColor: this.uFogColor,
      uFogParams: this.uFogParams,
      uSunScatter: this.uSunScatter,
      uWind: this.uWind,
      uPlayerPos: this.uPlayerPos,
      uPlayerVel: this.uPlayerVel,
      uSH: this.uSH,
      tTrail: this.tTrail,
      uTrailInfo: this.uTrailInfo,
      uTrailWin: this.uTrailWin,
      uAtmos: this.uAtmos,
    };
  }
}

export const color = (hex: string): Color => new Color(hex);
